import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { User, Shield, Sun, Mail, FileText, Plus, Edit2, X, Check, GitBranch, Trash2,  Type, Sliders, Upload, BookOpen, Download, Search, ListTree, Globe } from 'lucide-react';
import api from '../../api/client';
import ImportaCsvUtentiModal from '../../components/ImportaCsvUtentiModal';
import MatriceVociPortale from '../../components/MatriceVociPortale';
import EditorMenuLaterale from '../../components/EditorMenuLaterale';
import EditorMenuPortale from '../../components/EditorMenuPortale';
import FontiEsterne from '../../components/FontiEsterne';
import { validaCF, cfDaInserire } from '../../utils/codiceFiscale';

// Colori fissi per i ruoli "storici" — un ruolo custom nuovo usa un grigio neutro di fallback
// (vedi uso di `RUOLO_C[...] || '#888'` più sotto) finché non gli si assegna un colore dedicato.
const RUOLO_C = { ROOT:'#F4600C', ADMIN:'#3B82F6', OPERATORE:'#22C55E', ALLENATORE:'#A855F7', UTENTE:'#888' };

// ROOT e ALLENATORE non sono righe nella tabella Ruolo (sono ruoli "di sistema"/portale dedicato,
// vedi CLAUDE.md sezione 8-bis) — vanno sempre aggiunti a mano alla lista dei ruoli custom caricati
// dal server per ottenere l'elenco completo "chi è staff".
const staffRuoliDa = (ruoliCustom) => ['ROOT','ALLENATORE', ...ruoliCustom.map(r => r.nome)];

const BASE_TABS = [
  { id:'company',    label:'SOCIETÀ',       icon:Shield },
  { id:'users',      label:'UTENTI',        icon:User },
  { id:'statuses',   label:'STATI ORDINE',  icon:GitBranch },
  { id:'smtp',       label:'EMAIL / SMTP',  icon:Mail },
  { id:'docs',       label:'NUMERAZIONE',   icon:FileText },
  { id:'appearance', label:'ASPETTO',       icon:Sun },
];

// Stato di utilizzo a fasce (non la data esatta, che resta nel tooltip): basta capire se il portale viene usato.
// Riceve ultimo_accesso solo il ROOT (vedi users.service.ts → getAll).
const statoAccesso = (d) => {
  if (!d) return { label:'MAI ENTRATO', color:'#888' };
  const giorni = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (giorni < 7)   return { label:'QUESTA SETTIMANA', color:'#22C55E' };
  if (giorni < 30)  return { label:`${Math.floor(giorni/7)} SETT. FA`, color:'#22C55E' };
  const mesi = Math.floor(giorni / 30);
  if (mesi < 12)    return { label: mesi === 1 ? '1 MESE FA' : `${mesi} MESI FA`, color: mesi < 3 ? '#EAB308' : '#EF4444' };
  return { label:'OLTRE 1 ANNO', color:'#EF4444' };
};

export default function Settings() {
  const { user } = useAuth();
  const isRoot = user?.ruolo === 'ROOT';
  // GET /api/ruoli è leggibile anche da ADMIN (serve per classificare la lista utenti come
  // "staff" — un ruolo custom come OPERATORE non è più hardcoded, va per forza chiesto al
  // server). Solo ROOT può però creare/modificare/eliminare ruoli o assegnarli (vedi tab RUOLI
  // e form utenti più sotto, sempre gated su `isRoot`).
  const canReadRuoli = isRoot || user?.ruolo === 'ADMIN';
  const { prefs, update: updateTheme, reset: resetTheme } = useTheme();
  // ?tab=menu: il bottone "Modifica menu laterale" in fondo alla sidebar (solo ROOT) apre qui
  const location = useLocation();
  const [tab, setTab]                   = useState(() => new URLSearchParams(location.search).get('tab') || 'company');
  const [menuDi, setMenuDi]             = useState('STAFF'); // tab Menu laterale: STAFF | UTENTE | ALLENATORE
  useEffect(() => { const t = new URLSearchParams(location.search).get('tab'); if (t) setTab(t); }, [location.search]);
  const [company, setCompany]           = useState(null);
  const [users, setUsers]               = useState([]);
  // 21/10 titolare: cercare un utente senza la ricerca del browser
  const [filtroUtenti, setFiltroUtenti] = useState({ q:'', ruolo:'', stato:'' });
  const [companyForm, setCompanyForm]   = useState({});
  const [editingCompany, setEditingCompany] = useState(false);
  const [showNewUser, setShowNewUser]   = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [newUser, setNewUser]           = useState({ nome:'', cognome:'', email:'', password:'', ruolo:'', codice_fiscale:'' });
  const [editingUser, setEditingUser]   = useState(null);
  const [editUserForm, setEditUserForm] = useState({});
  const [orderStatuses, setOrderStatuses]   = useState([]);
  const [showNewStatus, setShowNewStatus]   = useState(false);
  const [newStatus, setNewStatus]           = useState({ nome:'', colore:'#888888', is_finale:false, is_conferma:false, is_consegna:false });
  const [editingStatus, setEditingStatus]   = useState(null);
  const [msg, setMsg]                   = useState('');
  const [smtpConfigs, setSmtpConfigs]   = useState([]);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestingId, setSmtpTestingId] = useState(null);
  const [smtpEditingId, setSmtpEditingId] = useState(null); // id esistente, 'new', o null
  const [smtpDraft, setSmtpDraft]       = useState({});
  const [smtpAssociazioni, setSmtpAssociazioni] = useState([]);
  const [invii, setInvii]               = useState(null); // registro email inviate, caricato aprendo la scheda
  const [filtroInvii, setFiltroInvii]   = useState({ esito:'', q:'' });
  const [accessi, setAccessi]           = useState([]);   // accessi SMTP (credenziali), separati dai mittenti
  const [accEditingId, setAccEditingId] = useState(null); // id, 'new' o null
  const [accDraft, setAccDraft]         = useState({});
  const [accProvaId, setAccProvaId]     = useState(null);
  const [ruoli, setRuoli]               = useState([]);
  const [ruoliModuli, setRuoliModuli]   = useState([]);
  const [sediNomi, setSediNomi]         = useState([]);
  const [selectedRuoloId, setSelectedRuoloId] = useState(null);
  const [ruoloDraft, setRuoloDraft]     = useState(null);

  const STAFF_RUOLI = staffRuoliDa(ruoli);
  const tabs = isRoot ? [...BASE_TABS, { id:'ruoli', label:'RUOLI', icon:Sliders }, { id:'menu', label:'MENU LATERALE', icon:ListTree }, { id:'fonti', label:'FONTI ESTERNE', icon:Globe }, { id:'documentazione', label:'DOCUMENTAZIONE', icon:BookOpen }] : BASE_TABS;

  const scaricaDocumentazione = async () => {
    try {
      const r = await api.get('/documentazione/tecnica', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Gestionale_BCheers_Documentazione_Tecnica.md';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast('Errore nel download della documentazione'); }
  };

  const toast = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 3000); };

  const load = async () => {
    // order-statuses richiede il permesso ordini: senza, la tab resta vuota invece di rompere la pagina
    const calls = [api.get('/company'), api.get('/users'), api.get('/order-statuses').catch(() => ({ data: [] })), api.get('/smtp-config'), api.get('/smtp-config/associazioni').catch(() => ({ data: [] })), api.get('/smtp-config/accessi').catch(() => ({ data: [] }))];
    if (canReadRuoli) calls.push(api.get('/ruoli'), api.get('/ruoli/moduli'), api.get('/planning/sedi-nomi').catch(() => ({ data: [] })));
    const [co, us, st, sc, ch, ac, rl, md, sn] = await Promise.all(calls);
    setAccessi(ac.data);
    setCompany(co.data); setCompanyForm(co.data);
    setUsers(us.data); setOrderStatuses(st.data); setSmtpConfigs(sc.data); setSmtpAssociazioni(ch.data);
    if (canReadRuoli) { setRuoli(rl.data); setRuoliModuli(md.data); setSediNomi(sn.data); if (!newUser.ruolo) setNewUser(f=>({...f, ruolo:'ALLENATORE'})); }
  };
  useEffect(() => { load(); }, []);

  const saveCompany   = async (e) => { if(e?.preventDefault) e.preventDefault(); try { await api.put('/company', companyForm); setEditingCompany(false); load(); toast('Salvato!'); } catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore salvataggio')); } };

  // ── Configurazioni SMTP (più mittenti: es. "Generale" per prenotazioni/rimborsi, "Contratti") ──
  const nuovaSmtp     = () => { setSmtpEditingId('new'); setSmtpDraft({ nome:'', smtp_from:'', smtp_enabled:true, accesso_id:null }); };
  const nuovoAccesso  = () => { setAccEditingId('new'); setAccDraft({ nome:'', host:'mail.b-cheers.it', porta:465, utente:'', password:'' }); };
  const salvaAccesso  = async () => {
    try {
      if (accEditingId === 'new') await api.post('/smtp-config/accessi', accDraft);
      else await api.put(`/smtp-config/accessi/${accEditingId}`, accDraft);
      setAccEditingId(null); setAccDraft({}); load(); toast('Salvato!');
    } catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore salvataggio')); }
  };
  const eliminaAccesso = async (a) => {
    if (!window.confirm(`Eliminare l'accesso "${a.nome}"? La password viene cancellata dal gestionale.`)) return;
    try { await api.delete(`/smtp-config/accessi/${a.id}`); load(); toast('Eliminato!'); }
    catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore eliminazione')); }
  };
  const provaAccesso  = async (id) => {
    setAccProvaId(id);
    try { const r = await api.post(`/smtp-config/accessi/${id}/prova`); toast('✓ ' + r.data.message); }
    catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
    finally { setAccProvaId(null); }
  };
  const caricaInvii   = async (f = filtroInvii) => {
    try { const r = await api.get('/smtp-config/invii', { params: { esito: f.esito || undefined, q: f.q || undefined } }); setInvii(r.data); }
    catch { setInvii([]); }
  };
  const modificaSmtp  = (cfg) => { setSmtpEditingId(cfg.id); setSmtpDraft({ nome:cfg.nome, smtp_from:cfg.smtp_from, smtp_enabled:cfg.smtp_enabled, accesso_id:cfg.accesso_id }); };
  const chiudiSmtp    = () => { setSmtpEditingId(null); setSmtpDraft({}); };
  const salvaSmtp     = async () => {
    try {
      if (smtpEditingId === 'new') await api.post('/smtp-config', smtpDraft);
      else await api.put(`/smtp-config/${smtpEditingId}`, smtpDraft);
      chiudiSmtp(); load(); if (invii) caricaInvii(); toast('Salvato!');
    } catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore salvataggio')); }
  };
  const eliminaSmtp   = async (id, nome) => {
    if (!window.confirm(`Eliminare il mittente "${nome}"? I servizi che lo usavano scriveranno dal principale.`)) return;
    try { await api.delete(`/smtp-config/${id}`); load(); toast('Eliminata!'); }
    catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore eliminazione')); }
  };
  const associaSmtp   = async (servizio, smtp_config_id) => {
    try { const r = await api.put(`/smtp-config/associazioni/${servizio}`, { smtp_config_id: smtp_config_id || null }); setSmtpAssociazioni(r.data); toast('Salvato!'); }
    catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore salvataggio')); }
  };
  const testSmtp      = async (id) => {
    setSmtpTestingId(id);
    try {
      const res = await api.post(`/smtp-config/${id}/test`, { email: smtpTestEmail || undefined });
      toast('✓ ' + res.data.message);
    } catch(err) {
      toast('✗ ' + (err.response?.data?.error || 'Errore invio'));
    } finally { setSmtpTestingId(null); }
  };
  const formAccesso = () => (
    <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:'12px', borderTop:'1px solid var(--border)' }}>
      {[['nome','NOME (solo per riconoscerlo)','text','es. Casella invio gestionale'],['host','SERVER SMTP','text','mail.b-cheers.it'],['porta','PORTA','number','465'],['utente','UTENTE SMTP','email','es. invio@b-cheers.it'],
        ['password','PASSWORD','password', accEditingId === 'new' ? '' : '•••••••• (lascia vuoto per non cambiarla)']].map(([k,l,t,ph]) => (
        <div key={k} style={fieldS}><label style={labelS}>{l}</label>
          <input style={inputS} type={t} placeholder={ph} autoComplete={t === 'password' ? 'new-password' : 'off'} value={accDraft[k] ?? ''} onChange={e=>setAccDraft(f=>({...f,[k]:e.target.value}))}/>
        </div>
      ))}
      <div style={{ display:'flex', gap:'8px' }}>
        <button style={btnPri} onClick={salvaAccesso}>{accEditingId === 'new' ? 'CREA ACCESSO' : 'SALVA'}</button>
        <button style={btnSec} onClick={() => { setAccEditingId(null); setAccDraft({}); }}>ANNULLA</button>
      </div>
    </div>
  );
  const formMittente = (cfg) => (
    <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:'12px', borderTop: cfg ? '1px solid var(--border)' : 'none' }}>
      <div style={fieldS}><label style={labelS}>NOME DEL MITTENTE (lo vede chi riceve)</label>
        <input style={inputS} value={smtpDraft.nome||''} placeholder="es. Segreteria B-Cheers ASD" onChange={e=>setSmtpDraft(f=>({...f,nome:e.target.value}))}/>
      </div>
      <div style={fieldS}><label style={labelS}>EMAIL MITTENTE</label>
        <input style={inputS} type="email" value={smtpDraft.smtp_from||''} placeholder="es. segreteria@b-cheers.it" onChange={e=>setSmtpDraft(f=>({...f,smtp_from:e.target.value}))}/>
      </div>
      <div style={fieldS}><label style={labelS}>PARTE CON L'ACCESSO</label>
        <select style={inputS} value={smtpDraft.accesso_id || ''} onChange={e=>setSmtpDraft(f=>({...f,accesso_id: e.target.value ? +e.target.value : null}))}>
          {cfg?.chiave !== 'DEFAULT' && <option value="">Quello del mittente principale</option>}
          {cfg?.chiave === 'DEFAULT' && !smtpDraft.accesso_id && <option value="">— scegli —</option>}
          {accessi.map(a => <option key={a.id} value={a.id}>{a.nome} ({a.utente})</option>)}
        </select>
      </div>
      <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)' }}>Usa un indirizzo dello stesso dominio dell'accesso (@b-cheers.it), altrimenti il server di posta può rifiutarlo o la mail finire nello spam.</div>
      <label style={{ display:'flex',alignItems:'center',gap:'10px' }}>
        <input type="checkbox" checked={smtpDraft.smtp_enabled||false} onChange={e=>setSmtpDraft(f=>({...f,smtp_enabled:e.target.checked}))} style={{ accentColor:'#F4600C' }}/>
        <span style={{ ...labelS,marginBottom:0 }}>{cfg?.chiave === 'DEFAULT' ? 'ATTIVO (spento = non parte nessuna email)' : 'ATTIVO (spento = i suoi servizi scrivono dal principale)'}</span>
      </label>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
        <button style={btnPri} onClick={salvaSmtp}>{cfg ? 'SALVA' : 'CREA MITTENTE'}</button>
        {cfg ? (<>
          <input style={{ ...inputS, flex:1, minWidth:'180px' }} type="email" placeholder={`Test a: ${user?.email}`} value={smtpTestEmail} onChange={e=>setSmtpTestEmail(e.target.value)}/>
          <button style={{ ...btnSec, opacity: smtpTestingId===cfg.id?0.7:1 }} disabled={smtpTestingId===cfg.id} onClick={() => testSmtp(cfg.id)}>{smtpTestingId===cfg.id ? 'INVIO...' : 'INVIA TEST'}</button>
        </>) : <button style={btnSec} onClick={chiudiSmtp}>ANNULLA</button>}
      </div>
    </div>
  );
  const createUser    = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post('/users', newUser);
      setShowNewUser(false); setNewUser({nome:'',cognome:'',email:'',password:'',ruolo:'ALLENATORE',codice_fiscale:''}); load();
      const a = r.data?.agganciato_a;
      toast(a ? `Utente creato e collegato alla scheda di ${a.cognome} ${a.nome} già in anagrafica` : 'Utente creato!');
    } catch(err) { toast('✗ ' + (err.response?.data?.error || 'Errore creazione utente')); }
  };
  const toggleUser    = async (id, attivo) => { await api.patch(`/users/${id}`, { attivo: !attivo }); load(); };
  const saveUser      = async (id) => { await api.put(`/users/${id}`, editUserForm); setEditingUser(null); load(); toast('Utente aggiornato!'); };
  const deleteUser    = async (id, nome) => { if(!window.confirm(`Eliminare l'utente ${nome}?`)) return; try { await api.delete(`/users/${id}`); load(); toast('Utente eliminato!'); } catch(err) { alert(err.response?.data?.error||'Errore'); } };
  const createStatus  = async (e) => { e.preventDefault(); await api.post('/order-statuses', newStatus); setShowNewStatus(false); setNewStatus({ nome:'', colore:'#888888', is_finale:false, is_conferma:false, is_consegna:false }); load(); toast('Stato creato!'); };
  const saveStatus    = async (id) => { await api.put(`/order-statuses/${id}`, editingStatus); setEditingStatus(null); load(); toast('Stato aggiornato!'); };
  const deleteStatus  = async (id) => { if(!window.confirm('Eliminare questo stato?')) return; try { await api.delete(`/order-statuses/${id}`); load(); } catch(err) { alert(err.response?.data?.error||'Errore'); } };

  // ── Ruoli custom ──────────────────────────────────────────────────────
  const permessiVuoti = () => Object.fromEntries(ruoliModuli.map(m => [m.modulo, { can_view:false, can_create:false, can_edit:false, can_delete:false }]));
  const draftDaRuolo  = (r) => ({
    id: r.id, nome: r.nome, descrizione: r.descrizione || '',
    permessi: { ...permessiVuoti(), ...Object.fromEntries(r.permessi.map(p => [p.modulo, { can_view:p.can_view, can_create:p.can_create, can_edit:p.can_edit, can_delete:p.can_delete }])) },
    sedi_planning: r.sedi_planning || [],
  });
  const selectRuolo = (r) => { setSelectedRuoloId(r.id); setRuoloDraft(draftDaRuolo(r)); };
  const newRuoloDraft = () => { setSelectedRuoloId('new'); setRuoloDraft({ id:null, nome:'', descrizione:'', permessi: permessiVuoti(), sedi_planning: [] }); };
  const togglePermesso = (modulo, azione) => setRuoloDraft(d => ({ ...d, permessi: { ...d.permessi, [modulo]: { ...d.permessi[modulo], [azione]: !d.permessi[modulo][azione] } } }));
  const toggleSedeRuolo = (nome) => setRuoloDraft(d => ({ ...d, sedi_planning: d.sedi_planning.includes(nome) ? d.sedi_planning.filter(n => n !== nome) : [...d.sedi_planning, nome] }));
  const salvaRuolo = async () => {
    const payload = {
      nome: ruoloDraft.nome.trim(),
      descrizione: ruoloDraft.descrizione,
      permessi: Object.entries(ruoloDraft.permessi).map(([modulo, p]) => ({ modulo, ...p })),
      sedi_planning: ruoloDraft.sedi_planning,
    };
    try {
      if (ruoloDraft.id) await api.put(`/ruoli/${ruoloDraft.id}`, payload);
      else { const res = await api.post('/ruoli', payload); setSelectedRuoloId(res.data.id); }
      await load();
      toast('Ruolo salvato!');
    } catch (err) { alert(err.response?.data?.error || 'Errore salvataggio ruolo'); }
  };
  const eliminaRuolo = async (id, nome) => {
    if (!window.confirm(`Eliminare il ruolo "${nome}"?`)) return;
    try { await api.delete(`/ruoli/${id}`); setSelectedRuoloId(null); setRuoloDraft(null); await load(); toast('Ruolo eliminato!'); }
    catch (err) { alert(err.response?.data?.error || 'Errore eliminazione ruolo'); }
  };

  const inputS = { background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'8px',padding:'9px 12px',color:'var(--text)',fontSize:'var(--font-size-sm)',outline:'none',width:'100%',boxSizing:'border-box' };
  const labelS = { fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)' };
  const fieldS = { display:'flex',flexDirection:'column',gap:'6px' };
  const btnPri = { background:'#F4600C',color:'#fff',border:'none',borderRadius:'8px',padding:'10px 18px',fontSize:'var(--font-size-xs)',fontWeight:'700',cursor:'pointer',display:'flex',alignItems:'center',gap:'8px' };
  const btnSec = { background:'var(--border)',color:'var(--text)',border:'1px solid var(--border2)',borderRadius:'8px',padding:'9px 14px',fontSize:'var(--font-size-xs)',fontWeight:'700',cursor:'pointer' };

  return (
    <div>
      {msg && (
        <div style={{ position:'fixed',top:'20px',right:'20px',background:'#22C55E',color:'#fff',padding:'12px 20px',borderRadius:'8px',fontWeight:'700',fontSize:'var(--font-size-sm)',zIndex:9999,display:'flex',alignItems:'center',gap:'8px' }}>
          <Check size={14}/> {msg}
        </div>
      )}

      <h1 style={{ fontFamily:"'Barlow Condensed'",fontSize: 'var(--font-size-title)',fontWeight:'800',letterSpacing:'2px',marginBottom:'24px' }}>IMPOSTAZIONI</h1>

      {/* TABS */}
      <div style={{ display:'flex',gap:'4px',marginBottom:'28px',borderBottom:'1px solid var(--border)',paddingBottom:'0' }}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={()=>setTab(id)}
            style={{ display:'flex',alignItems:'center',gap:'7px',padding:'10px 16px',background:'transparent',border:'none',borderBottom: tab===id ? '2px solid #F4600C':'2px solid transparent',color: tab===id ? 'var(--text)':'var(--text-dim)',fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'1px',cursor:'pointer',marginBottom:'-1px' }}>
            <Icon size={13}/> {label}
          </button>
        ))}
      </div>

      {/* ── SOCIETÀ ── */}
      {tab === 'company' && company && (
        <div style={{ maxWidth:'600px' }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px' }}>
            <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px' }}>DATI SOCIETARI</h2>
            <button style={btnPri} onClick={()=>setEditingCompany(!editingCompany)}><Edit2 size={13}/> MODIFICA</button>
          </div>
          {editingCompany ? (
            <form onSubmit={saveCompany} style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'24px',display:'flex',flexDirection:'column',gap:'14px' }}>
              {[['ragione_sociale','RAGIONE SOCIALE'],['partita_iva','PARTITA IVA'],['codice_fiscale','CODICE FISCALE'],['regime_fiscale','REGIME FISCALE'],['indirizzo','INDIRIZZO'],['cap','CAP'],['citta','CITTÀ'],['provincia','PROVINCIA'],['telefono','TELEFONO'],['email','EMAIL']].map(([k,l]) => (
                <div key={k} style={fieldS}><label style={labelS}>{l}</label><input style={inputS} value={companyForm[k]||''} onChange={e=>setCompanyForm(f=>({...f,[k]:e.target.value}))}/></div>
              ))}
              <div style={{ display:'flex',gap:'10px',justifyContent:'flex-end' }}>
                <button type="button" style={btnSec} onClick={()=>setEditingCompany(false)}>ANNULLA</button>
                <button type="submit" style={btnPri}>SALVA</button>
              </div>
            </form>
          ) : (
            <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'24px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px' }}>
              {[['Ragione Sociale','ragione_sociale'],['Partita IVA','partita_iva'],['Codice Fiscale','codice_fiscale'],['Regime Fiscale','regime_fiscale'],['Indirizzo','indirizzo'],['Città','citta'],['Telefono','telefono'],['Email','email']].map(([l,k]) => (
                <div key={k}><div style={{ ...labelS,marginBottom:'4px' }}>{l}</div><div style={{ fontSize:'var(--font-size-sm)',color:'var(--text)' }}>{company[k]||'—'}</div></div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── UTENTI ── */}
      {tab === 'users' && (() => {
        const norm = (x) => (x || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const parole = norm(filtroUtenti.q).split(/\s+/).filter(Boolean);
        const passa = (u) => {
          if (filtroUtenti.ruolo && u.ruolo !== filtroUtenti.ruolo) return false;
          if (filtroUtenti.stato === 'attivi' && !u.attivo) return false;
          if (filtroUtenti.stato === 'disattivati' && u.attivo) return false;
          if (!parole.length) return true;
          const testo = norm([u.nome, u.nome_proprio, u.cognome, u.email, u.ruolo].join(' '));
          return parole.every(w => testo.includes(w));
        };
        const filtriAttivi = !!(filtroUtenti.q.trim() || filtroUtenti.ruolo || filtroUtenti.stato);
        const tuttiStaff = users.filter(u => STAFF_RUOLI.includes(u.ruolo));
        const tuttiSoci  = users.filter(u => u.ruolo === 'UTENTE');
        const staff  = tuttiStaff.filter(passa);
        const soci   = tuttiSoci.filter(passa);
        const ruoliPresenti = [...new Set(users.map(u => u.ruolo))].sort();

        const UserRow = ({ u }) => (
          <div key={u.id} style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'10px',padding:'14px 20px',opacity:u.attivo?1:0.6 }}>
            {editingUser === u.id ? (
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'10px',alignItems:'end' }}>
                <div style={fieldS}><label style={labelS}>NOME (ANCHE IL SECONDO)</label>
                  <input style={inputS} value={editUserForm.nome||''} onChange={e=>setEditUserForm(f=>({...f,nome:e.target.value}))}/>
                </div>
                <div style={fieldS}><label style={labelS}>COGNOME</label>
                  <input style={inputS} value={editUserForm.cognome||''} onChange={e=>setEditUserForm(f=>({...f,cognome:e.target.value}))}/>
                </div>
                <div style={fieldS}><label style={labelS}>EMAIL</label>
                  <input style={inputS} value={editUserForm.email||''} onChange={e=>setEditUserForm(f=>({...f,email:e.target.value}))}/>
                </div>
                <div style={fieldS}><label style={labelS}>RUOLO</label>
                  {isRoot ? (
                    <>
                      <select style={inputS} value={editUserForm.ruolo||''} onChange={e=>setEditUserForm(f=>({...f,ruolo:e.target.value}))}>
                        {/* UTENTE/ALLENATORE nascosti per chi è già collegato come allenatore (is_allenatore):
                            selezionarli qui sovrascriverebbe il suo ruolo staff invece di aggiungersi ad esso
                            (è già "anche allenatore" tramite il collegamento in Dipendenti) — vedi caso Enrico
                            Mautino 2026-10-01, risolto manualmente dopo che questa trappola gli aveva cancellato
                            il ruolo "Operatore ENRICO". Per cambiare davvero il suo stato di allenatore, usare
                            "Scollega utente" nella sua Scheda Dipendente. */}
                        {!u.is_allenatore && <option value="UTENTE">UTENTE (cliente)</option>}
                        {!u.is_allenatore && <option value="ALLENATORE">ALLENATORE</option>}
                        {ruoli.map(r=><option key={r.nome} value={r.nome}>{r.nome}</option>)}
                      </select>
                      {u.is_allenatore && (
                        <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'4px' }}>
                          Già collegato come allenatore — per cambiarlo usa "Scollega utente" nella sua Scheda Dipendente.
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ ...inputS, background:'transparent', border:'1px solid transparent', padding:'9px 0', color:'var(--text-dim)' }} title="Solo il ROOT può cambiare il ruolo">{u.ruolo}</div>
                  )}
                </div>
                <div style={fieldS}><label style={labelS}>NUOVA PASSWORD (opz.)</label>
                  <input style={inputS} type="password" placeholder="lascia vuoto per non cambiare"
                    value={editUserForm.password||''} onChange={e=>setEditUserForm(f=>({...f,password:e.target.value}))}/>
                </div>
                <div style={{ display:'flex',alignItems:'center',gap:'12px',gridColumn:'1/-1',paddingTop:'4px' }}>
                  <label style={{ display:'flex',alignItems:'center',gap:'8px',cursor:'pointer' }}>
                    <input type="checkbox" checked={!!editUserForm.is_socio} onChange={e=>setEditUserForm(f=>({...f,is_socio:e.target.checked}))} style={{ accentColor:'#22C55E',width:'15px',height:'15px' }}/>
                    <span style={{ fontSize:'var(--font-size-xs)',fontWeight:'700',color:'#22C55E' }}>SOCIO</span>
                    <span style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }}>— è socio dell'associazione</span>
                  </label>
                  {isRoot && (
                    <label style={{ display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',marginLeft:'12px' }}>
                      <input type="checkbox" checked={!!editUserForm.is_direttivo} onChange={e=>setEditUserForm(f=>({...f,is_direttivo:e.target.checked}))} style={{ accentColor:'#F4600C',width:'15px',height:'15px' }}/>
                      <span style={{ fontSize:'var(--font-size-xs)',fontWeight:'700',color:'#F4600C' }}>DIRETTIVO</span>
                      <span style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }}>— vede i compiti degli altri membri del direttivo</span>
                    </label>
                  )}
                </div>
                <div style={{ display:'flex',gap:'8px',alignItems:'flex-end',gridColumn:'1/-1',justifyContent:'flex-end' }}>
                  <button style={btnSec} onClick={()=>setEditingUser(null)}>ANNULLA</button>
                  <button style={btnPri} onClick={()=>saveUser(u.id)}><Check size={13}/> SALVA</button>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                <div style={{ width:'36px',height:'36px',background:`${(RUOLO_C[u.ruolo]||'#888')}20`,border:`1px solid ${(RUOLO_C[u.ruolo]||'#888')}30`,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  <span style={{ color:RUOLO_C[u.ruolo]||'#888',fontWeight:'800',fontSize:'var(--font-size-sm)' }}>{u.nome?.[0]}</span>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'700',fontSize:'var(--font-size-sm)' }}>{u.nome}</div>
                  <div style={{ color:'var(--text-dim)',fontSize:'var(--font-size-xs)' }}>{u.email}</div>
                </div>
                <span style={{ background:`${(RUOLO_C[u.ruolo]||'#888')}20`,color:RUOLO_C[u.ruolo]||'#888',border:`1px solid ${(RUOLO_C[u.ruolo]||'#888')}40`,borderRadius:'4px',padding:'2px 8px',fontSize: 'var(--font-size-xs)',fontWeight:'700' }}>{u.ruolo}</span>
                {u.is_socio && <span style={{ background:'#22C55E20',color:'#22C55E',border:'1px solid #22C55E40',borderRadius:'4px',padding:'2px 8px',fontSize: 'var(--font-size-xs)',fontWeight:'700' }}>SOCIO</span>}
                {u.is_direttivo && <span style={{ background:'#F4600C20',color:'#F4600C',border:'1px solid #F4600C40',borderRadius:'4px',padding:'2px 8px',fontSize: 'var(--font-size-xs)',fontWeight:'700' }}>DIRETTIVO</span>}
                {u.email_verificata === false && (
                  // registrato ma codice di conferma mai inserito: non può entrare (21/10/2026)
                  <button title="Non ha mai inserito il codice ricevuto via email, quindi non può entrare. Clicca per confermarla tu (fallo solo se sei sicuro che l'email sia sua)."
                    style={{ background:'#F59E0B20',color:'#B45309',border:'1px solid #F59E0B60',borderRadius:'4px',padding:'2px 8px',fontSize:'var(--font-size-xs)',fontWeight:'700',whiteSpace:'nowrap',cursor:'pointer' }}
                    onClick={async () => {
                      if (!window.confirm(`${u.nome} non ha mai confermato l'email ${u.email} e non riesce a entrare.\n\nConfermarla adesso? Fallo solo se sei sicuro che l'indirizzo sia suo.`)) return;
                      try { await api.post(`/users/${u.id}/conferma-email`); load(); } catch (e) { alert(e.response?.data?.error || 'Errore'); }
                    }}>EMAIL NON CONFERMATA · CONFERMA</button>
                )}
                {isRoot && (() => { const st = statoAccesso(u.ultimo_accesso); return (
                  <span title={u.ultimo_accesso ? `Ultimo accesso: ${new Date(u.ultimo_accesso).toLocaleString('it-IT')}` : 'Nessun accesso registrato'}
                    style={{ background:`${st.color}20`,color:st.color,border:`1px solid ${st.color}40`,borderRadius:'4px',padding:'2px 8px',fontSize:'var(--font-size-xs)',fontWeight:'700',whiteSpace:'nowrap' }}>{st.label}</span>
                ); })()}
                <span style={{ fontSize:'var(--font-size-xs)',fontWeight:'700',color:u.attivo?'#22C55E':'#EF4444' }}>{u.attivo?'● ATTIVO':'● DISATTIVO'}</span>
                {u.email !== user.email ? (
                  <>
                    {(user.ruolo==='ROOT' || !['ADMIN','ROOT'].includes(u.ruolo)) ? (
                      <>
                        <button style={{ ...btnSec,padding:'6px 10px' }} title="Modifica"
                          onClick={()=>{ setEditingUser(u.id); setEditUserForm({nome:u.nome_proprio,cognome:u.cognome,email:u.email,ruolo:u.ruolo,password:'',is_socio:!!u.is_socio,is_direttivo:!!u.is_direttivo}); }}>
                          <Edit2 size={13}/>
                        </button>
                        <button style={{ ...btnSec,padding:'6px 12px' }} onClick={()=>toggleUser(u.id,u.attivo)}>
                          {u.attivo?'DISATTIVA':'ATTIVA'}
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }} title="Solo il ROOT può modificare un altro amministratore">gestito da ROOT</span>
                    )}
                    {user.ruolo==='ROOT' && u.ruolo!=='ROOT' && (
                      <button title="Elimina" onClick={()=>deleteUser(u.id,u.nome)}
                        style={{ background:'#EF444415',border:'1px solid #EF444430',borderRadius:'8px',padding:'6px 10px',color:'#EF4444',cursor:'pointer' }}>
                        <Trash2 size={13}/>
                      </button>
                    )}
                  </>
                ) : (
                  <span style={{ background:'#F4600C20',color:'#F4600C',border:'1px solid #F4600C40',borderRadius:'6px',padding:'4px 10px',fontSize: 'var(--font-size-xs)',fontWeight:'700' }}>TU</span>
                )}
              </div>
            )}
          </div>
        );

        return (
          <div>
            {/* Header */}
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px' }}>
              <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px' }}>GESTIONE UTENTI</h2>
              <div style={{ display:'flex',gap:'8px' }}>
                <button style={btnSec} onClick={()=>setShowImportCsv(true)}><Upload size={13}/> IMPORTA DA CSV</button>
                <button style={btnPri} onClick={()=>setShowNewUser(true)}><Plus size={13}/> NUOVO UTENTE</button>
              </div>
            </div>
            {showImportCsv && <ImportaCsvUtentiModal onClose={()=>setShowImportCsv(false)} onDone={load} />}
            {!isRoot && (
              <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }}>
                Puoi creare utenti cliente e allenatore; solo il ROOT può creare o promuovere utenti staff. Tu puoi comunque modificare/disattivare gli utenti che già gestisci.
              </div>
            )}

            {/* Form nuovo utente staff — solo ROOT (unico che può assegnare ruoli staff) */}
            {showNewUser && (
              <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'20px',marginBottom:'16px' }}>
                <div style={{ fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)',marginBottom:'14px' }}>CREA UTENTE — cliente, allenatore o staff (staff solo ROOT)</div>
                <form onSubmit={createUser} style={{ display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'12px',alignItems:'flex-end' }}>
                  {[['nome','NOME (ANCHE IL SECONDO)'],['cognome','COGNOME'],['email','EMAIL'],['password','PASSWORD']].map(([k,l]) => (
                    <div key={k} style={fieldS}><label style={labelS}>{l}</label>
                      <input style={inputS} type={k==='password'?'password':'text'} value={newUser[k]} onChange={e=>setNewUser(f=>({...f,[k]:e.target.value}))} required />
                    </div>
                  ))}
                  <div style={fieldS}><label style={labelS}>RUOLO</label>
                    <select style={inputS} value={newUser.ruolo} onChange={e=>setNewUser(f=>({...f,ruolo:e.target.value}))}>
                      <option value="UTENTE">UTENTE (cliente)</option>
                      <option value="ALLENATORE">ALLENATORE</option>
                      {isRoot && ruoli.map(r=><option key={r.nome} value={r.nome}>{r.nome}</option>)}
                    </select>
                  </div>
                  {(() => {
                    // Conta il codice fiscale: se è già in anagrafica l'account si aggancia a quella persona.
                    const portale = newUser.ruolo === 'UTENTE' || newUser.ruolo === 'ALLENATORE';
                    const cf = newUser.codice_fiscale.trim();
                    const v = cf && !cfDaInserire(cf) ? validaCF(cf, newUser.nome, newUser.cognome) : null;
                    return (
                      <div style={{ ...fieldS, gridColumn:'1/-1' }}><label style={labelS}>CODICE FISCALE {portale ? '' : '(facoltativo per lo staff)'}</label>
                        <input style={{ ...inputS, textTransform:'uppercase', maxWidth:'280px' }} value={newUser.codice_fiscale} maxLength={16}
                          onChange={e=>setNewUser(f=>({...f,codice_fiscale:e.target.value.toUpperCase()}))} required={portale} placeholder="se manca: AAAAAAAAAAAAAAAA" />
                        <span style={{ fontSize:'var(--font-size-xs)', color: v && !v.valido ? '#EF4444' : 'var(--text-muted)' }}>
                          {cfDaInserire(cf) ? 'CF da inserire: la scheda nasce senza codice fiscale.'
                            : !v ? 'Se il codice fiscale è già in anagrafica, l\'account viene collegato a quella persona.'
                            : !v.valido ? v.errore
                            : v.parziale ? 'Inserisci nome e cognome per verificare il codice fiscale.'
                            : '✓ Codice fiscale corretto'}
                        </span>
                      </div>
                    );
                  })()}
                  <div style={{ gridColumn:'1/-1',display:'flex',gap:'10px',justifyContent:'flex-end' }}>
                    <button type="button" style={btnSec} onClick={()=>setShowNewUser(false)}>ANNULLA</button>
                    <button type="submit" style={btnPri}>CREA</button>
                  </div>
                </form>
              </div>
            )}

            {/* RICERCA E FILTRI */}
            <div style={{ display:'flex',flexWrap:'wrap',gap:'8px',alignItems:'center',marginBottom:'18px' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'6px',flex:'1 1 260px',maxWidth:'420px',background:'var(--bg-card)',border:'1px solid var(--border2)',borderRadius:'999px',padding:'0 14px' }}>
                <Search size={15} color="var(--text-dim)"/>
                <input autoFocus value={filtroUtenti.q} onChange={e=>setFiltroUtenti(f=>({...f,q:e.target.value}))} placeholder="Cerca nome, cognome o email…"
                  style={{ border:'none',background:'transparent',outline:'none',boxShadow:'none',padding:'9px 0',width:'100%',color:'var(--text)',fontSize:'14px' }}/>
                {filtroUtenti.q && <button type="button" onClick={()=>setFiltroUtenti(f=>({...f,q:''}))} style={{ border:'none',background:'none',cursor:'pointer',color:'var(--text-dim)',padding:0,display:'flex' }} title="Svuota"><X size={15}/></button>}
              </div>
              <select style={{ ...inputS,width:'auto',borderRadius:'999px' }} value={filtroUtenti.ruolo} onChange={e=>setFiltroUtenti(f=>({...f,ruolo:e.target.value}))}>
                <option value="">Tutti i ruoli</option>
                {ruoliPresenti.map(r => <option key={r} value={r}>{r === 'UTENTE' ? 'UTENTE (cliente)' : r} ({users.filter(u=>u.ruolo===r).length})</option>)}
              </select>
              <select style={{ ...inputS,width:'auto',borderRadius:'999px' }} value={filtroUtenti.stato} onChange={e=>setFiltroUtenti(f=>({...f,stato:e.target.value}))}>
                <option value="">Attivi e disattivati</option>
                <option value="attivi">Solo attivi</option>
                <option value="disattivati">Solo disattivati</option>
              </select>
              {filtriAttivi && <>
                <span style={{ fontSize:'var(--font-size-xs)',color:'var(--text-muted)' }}>{staff.length + soci.length} di {tuttiStaff.length + tuttiSoci.length}</span>
                <button type="button" style={btnSec} onClick={()=>setFiltroUtenti({ q:'', ruolo:'', stato:'' })}>TOGLI FILTRI</button>
              </>}
            </div>

            {/* SEZIONE STAFF */}
            {(!filtriAttivi || staff.length > 0) && <div style={{ marginBottom:'28px' }}>
              <div style={{ fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)',marginBottom:'10px',display:'flex',alignItems:'center',gap:'8px' }}>
                <Shield size={11}/> STAFF — {filtriAttivi ? `${staff.length} di ${tuttiStaff.length}` : staff.length} utenti
              </div>
              {/* Legenda ruoli — ADMIN/OPERATORE/custom vengono dal server, ALLENATORE è fisso (portale dedicato) */}
              {!filtriAttivi && <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(190px,1fr))',gap:'8px',marginBottom:'14px' }}>
                {[
                  ...ruoli.map(r => ({ nome:r.nome, desc: r.descrizione || `${r.permessi.filter(p=>p.can_view).length}/${r.permessi.length} moduli visibili` })),
                  { nome:'ALLENATORE', desc:'Coach + accesso portale cliente' },
                ].map(r => (
                  <div key={r.nome} style={{ background:'var(--bg-card)',border:`1px solid ${(RUOLO_C[r.nome]||'#888')}30`,borderRadius:'8px',padding:'10px 12px' }}>
                    <div style={{ background:`${(RUOLO_C[r.nome]||'#888')}20`,color:RUOLO_C[r.nome]||'#888',borderRadius:'4px',padding:'2px 8px',fontSize: 'var(--font-size-xs)',fontWeight:'700',letterSpacing:'1px',display:'inline-block',marginBottom:'4px' }}>{r.nome}</div>
                    <div style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)',lineHeight:'1.4' }}>{r.desc}</div>
                  </div>
                ))}
              </div>}
              {!filtriAttivi && isRoot && <div style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)',marginBottom:'14px' }}>Gestisci i ruoli e i loro permessi dalla tab <b>RUOLI</b> qui sopra.</div>}
              <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
                {staff.map(u => <React.Fragment key={u.id}>{UserRow({u})}</React.Fragment>)}
              </div>
            </div>}

            {filtriAttivi && staff.length + soci.length === 0 && (
              <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'10px',padding:'20px',textAlign:'center',color:'var(--text-dim)',fontSize:'var(--font-size-sm)' }}>Nessun utente trovato con questi filtri.</div>
            )}

            {/* SEZIONE SOCI / CLIENTI */}
            {(!filtriAttivi || soci.length > 0) && <div>
              <div style={{ fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)',marginBottom:'10px',display:'flex',alignItems:'center',gap:'8px' }}>
                <User size={11}/> SOCI / CLIENTI — {filtriAttivi ? `${soci.length} di ${tuttiSoci.length}` : soci.length} registrati
                <span style={{ fontWeight:'400',color:'var(--text-dim)',fontSize:'var(--font-size-xs)',letterSpacing:'0' }}>· si registrano autonomamente · puoi elevarli ad Allenatore</span>
              </div>
              {soci.length === 0 ? (
                <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'10px',padding:'20px',textAlign:'center',color:'var(--text-dim)',fontSize:'var(--font-size-sm)' }}>
                  Nessun socio registrato ancora
                </div>
              ) : (
                <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
                  {soci.map(u => <React.Fragment key={u.id}>{UserRow({u})}</React.Fragment>)}
                </div>
              )}
            </div>}
          </div>
        );
      })()}

      {/* ── RUOLI (solo ROOT) ── */}
      {/* ── DOCUMENTAZIONE TECNICA (solo ROOT) ── */}
      {tab === 'documentazione' && isRoot && (
        <div style={{ maxWidth:'640px' }}>
          <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-xl)',fontWeight:'700',letterSpacing:'2px' }}>DOCUMENTAZIONE TECNICA</h2>
          <p style={{ color:'var(--text-dim)',fontSize:'var(--font-size-xs)',marginTop:'4px',marginBottom:'20px' }}>Riservata a ROOT</p>
          <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'20px' }}>
            <p style={{ fontSize:'var(--font-size-sm)',color:'var(--text-muted)',lineHeight:1.6,marginBottom:'16px' }}>
              Descrizione completa del gestionale per un programmatore: architettura, linguaggi, server, database, pagine,
              parte grafica, funzionalità, permessi, sicurezza e comandi operativi. Formato Markdown (.md), leggibile con
              qualsiasi editor di testo. Non contiene password né chiavi.
            </p>
            <button onClick={scaricaDocumentazione}
              style={{ display:'flex',alignItems:'center',gap:'8px',padding:'10px 18px',background:'#F4600C',color:'#fff',border:'none',borderRadius:'8px',fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'1px' }}>
              <Download size={14}/> SCARICA DOCUMENTAZIONE
            </button>
          </div>
        </div>
      )}

      {tab === 'ruoli' && isRoot && (() => {
        const draft = ruoloDraft;
        return (
          <div>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px' }}>
              <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px' }}>GESTIONE RUOLI</h2>
            </div>
            <div style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)',marginBottom:'20px',maxWidth:'640px',lineHeight:'1.5' }}>
              Per ogni ruolo decidi cosa può vedere, creare, modificare o eliminare in ciascun modulo. Il ruolo <b>ROOT</b> (il tuo) ha sempre accesso completo a tutto e non compare qui perché non è modificabile.
            </div>

            <div style={{ display:'flex',gap:'20px',alignItems:'flex-start' }}>

              {/* Lista ruoli */}
              <div style={{ width:'260px',flexShrink:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',overflow:'hidden' }}>
                <div style={{ padding:'12px 14px 8px',fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'1px',color:'var(--text-dim)' }}>RUOLI ({ruoli.length})</div>
                <div style={{ display:'flex',flexDirection:'column',padding:'0 8px 8px',gap:'2px' }}>
                  {ruoli.map(r => (
                    <button key={r.id} onClick={()=>selectRuolo(r)}
                      style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px',padding:'9px 10px',border:'none',borderRadius:'8px',cursor:'pointer',textAlign:'left',
                        background: selectedRuoloId===r.id ? '#F4600C15' : 'transparent',
                        borderLeft: selectedRuoloId===r.id ? '3px solid #F4600C' : '3px solid transparent' }}>
                      <span style={{ fontSize:'var(--font-size-sm)',fontWeight:'600',color: selectedRuoloId===r.id ? 'var(--text)' : 'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{r.nome}</span>
                      <span style={{ fontSize:'var(--font-size-xs)',fontWeight:'700',color:'var(--text-dim)',background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'20px',padding:'2px 8px',flexShrink:0 }}>{r.n_utenti}</span>
                    </button>
                  ))}
                </div>
                <div style={{ padding:'8px 10px 12px' }}>
                  <button onClick={newRuoloDraft} style={{ width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',background:'transparent',border:'1px dashed var(--border2)',color:'var(--text-dim)',fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'0.3px',padding:'9px',borderRadius:'8px',cursor:'pointer' }}>
                    <Plus size={13}/> NUOVO RUOLO
                  </button>
                </div>
              </div>

              {/* Dettaglio ruolo */}
              <div style={{ flex:1,minWidth:0 }}>
                {!draft ? (
                  <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'40px',textAlign:'center',color:'var(--text-dim)',fontSize:'var(--font-size-sm)' }}>
                    Seleziona un ruolo dalla lista, o creane uno nuovo.
                  </div>
                ) : (
                  <div style={{ display:'flex',flexDirection:'column',gap:'16px' }}>
                    <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'18px 20px',display:'flex',flexDirection:'column',gap:'12px' }}>
                      <div style={{ display:'flex',gap:'14px',flexWrap:'wrap' }}>
                        <div style={{ ...fieldS,flex:1,minWidth:'200px' }}>
                          <label style={labelS}>NOME RUOLO</label>
                          <input style={inputS} value={draft.nome} onChange={e=>setRuoloDraft(d=>({...d,nome:e.target.value}))} placeholder="es. Magazziniere"/>
                        </div>
                        {draft.id && (
                          <div style={{ display:'flex',alignItems:'center',gap:'8px' }}>
                            <span style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }}>Utenti con questo ruolo</span>
                            <span style={{ fontSize:'var(--font-size-sm)',fontWeight:'700',background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'20px',padding:'3px 11px' }}>
                              {ruoli.find(r=>r.id===draft.id)?.n_utenti ?? 0}
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={fieldS}>
                        <label style={labelS}>DESCRIZIONE (facoltativa)</label>
                        <input style={inputS} value={draft.descrizione} onChange={e=>setRuoloDraft(d=>({...d,descrizione:e.target.value}))} placeholder="a cosa serve questo ruolo"/>
                      </div>
                    </div>

                    {/* Matrice permessi */}
                    <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',overflow:'hidden' }}>
                      <div style={{ display:'flex',alignItems:'center',padding:'11px 18px',borderBottom:'1px solid var(--border)',background:'var(--bg-input)' }}>
                        <div style={{ flex:1,fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'0.5px',color:'var(--text-dim)' }}>MODULO</div>
                        {['VEDI','CREA','MODIFICA','ELIMINA'].map(l => (
                          <div key={l} style={{ width:'80px',textAlign:'center',fontSize:'var(--font-size-xs)',fontWeight:'700',color:'var(--text-dim)' }}>{l}</div>
                        ))}
                      </div>
                      {ruoliModuli.map((m, i) => {
                        const p = draft.permessi[m.modulo] || { can_view:false, can_create:false, can_edit:false, can_delete:false };
                        const nuovoGruppo = m.gruppo && m.gruppo !== ruoliModuli[i - 1]?.gruppo;
                        return (
                          <React.Fragment key={m.modulo}>
                          {nuovoGruppo && <div style={{ padding:'10px 18px 6px',fontSize:'var(--font-size-xs)',fontWeight:'800',letterSpacing:'0.6px',color:'#F4600C',background:'var(--bg-input)',borderBottom:'1px solid var(--border)' }}>{m.gruppo.toUpperCase()}</div>}
                          <div style={{ display:'flex',alignItems:'center',padding:'9px 18px',borderBottom:'1px solid var(--border)' }}>
                            <div style={{ flex:1,minWidth:0,paddingRight:'10px' }}>
                              <div style={{ fontSize:'var(--font-size-sm)',fontWeight:600,color:'var(--text)' }}>{m.label}</div>
                              {m.spiega && <div style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)',lineHeight:1.45,marginTop:'2px' }}>{m.spiega}</div>}
                            </div>
                            {['can_view','can_create','can_edit','can_delete'].map(azione => (
                              <div key={azione} style={{ width:'80px',display:'flex',justifyContent:'center' }}>
                                <input type="checkbox" checked={!!p[azione]} onChange={()=>togglePermesso(m.modulo, azione)}
                                  style={{ width:'16px',height:'16px',accentColor:'#F4600C',cursor:'pointer' }}/>
                              </div>
                            ))}
                          </div>
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {/* Sedi consentite in Planning Corsi / Pagamento Corsi */}
                    <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'16px 18px',display:'flex',flexDirection:'column',gap:'10px' }}>
                      <div>
                        <div style={{ fontSize:'var(--font-size-xs)',fontWeight:'700',letterSpacing:'0.5px',color:'var(--text-dim)',marginBottom:'4px' }}>SEDI CONSENTITE — PLANNING E PAGAMENTO CORSI</div>
                        <div style={{ fontSize:'var(--font-size-xs)',color:'var(--text-dim)',lineHeight:'1.5' }}>
                          Nessuna selezionata = vede tutte le sedi (comportamento invariato). Selezionandone una o più, questo ruolo vedrà e potrà gestire (turni, atleti, pagamenti) <b>solo</b> quelle sedi, in ogni ciclo attuale e futuro. Non riguarda la tab "Lezioni singole" (nessun collegamento a sede nel database).
                        </div>
                      </div>
                      {sediNomi.length === 0 ? (
                        <div style={{ fontSize:'var(--font-size-xs)', color:'#555' }}>Nessuna sede creata finora in Planning Corsi.</div>
                      ) : (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                          {sediNomi.map(nome => {
                            const attiva = draft.sedi_planning.includes(nome);
                            return (
                              <button key={nome} type="button" onClick={()=>toggleSedeRuolo(nome)}
                                style={{ fontSize:'var(--font-size-xs)', fontWeight:'600', padding:'6px 12px', borderRadius:'99px', cursor:'pointer',
                                  border: attiva ? '1px solid #F4600C' : '1px solid var(--border2)',
                                  background: attiva ? '#F4600C20' : 'transparent',
                                  color: attiva ? '#F4600C' : 'var(--text-muted)' }}>
                                {nome}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Azioni */}
                    <div style={{ display:'flex',justifyContent:'flex-end',gap:'10px' }}>
                      {draft.id && (
                        <button onClick={()=>eliminaRuolo(draft.id, draft.nome)} style={{ ...btnSec, color:'#EF4444', borderColor:'#EF444430' }}>
                          <Trash2 size={13}/> ELIMINA RUOLO
                        </button>
                      )}
                      <button style={btnPri} onClick={salvaRuolo} disabled={!draft.nome.trim()}>
                        <Check size={13}/> SALVA
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── STATI ORDINE ── */}
      {tab === 'ruoli' && isRoot && <MatriceVociPortale/>}

      {tab === 'menu' && isRoot && (
        <div>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'16px' }}>
            {[['STAFF','STAFF'],['UTENTE','PORTALE UTENTI'],['ALLENATORE','PORTALE ALLENATORI']].map(([id, label]) => (
              <button key={id} onClick={() => setMenuDi(id)}
                style={{ padding:'7px 14px', borderRadius:'999px', border: menuDi===id ? '1px solid #F4600C' : '1px solid var(--border)', background: menuDi===id ? '#F4600C' : 'transparent', color: menuDi===id ? '#fff' : 'var(--text)', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', cursor:'pointer' }}>
                {label}
              </button>
            ))}
          </div>
          {menuDi === 'STAFF' ? <EditorMenuLaterale/> : <EditorMenuPortale key={menuDi} ambito={menuDi}/>}
        </div>
      )}

      {tab === 'fonti' && isRoot && <FontiEsterne/>}

      {tab === 'statuses' && (
        <div>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px' }}>
            <div>
              <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px' }}>STATI ORDINE</h2>
              <p style={{ color:'var(--text-dim)',fontSize:'var(--font-size-xs)',marginTop:'4px' }}>Definisci il flusso degli ordini</p>
            </div>
            <button style={btnPri} onClick={()=>setShowNewStatus(true)}><Plus size={13}/> NUOVO STATO</button>
          </div>

          {showNewStatus && (
            <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'20px',marginBottom:'16px' }}>
              <form onSubmit={createStatus} style={{ display:'flex',flexDirection:'column',gap:'12px' }}>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 120px',gap:'12px' }}>
                  <div style={fieldS}><label style={labelS}>NOME STATO *</label>
                    <input style={inputS} value={newStatus.nome} onChange={e=>setNewStatus(f=>({...f,nome:e.target.value.toUpperCase()}))} required />
                  </div>
                  <div style={fieldS}><label style={labelS}>COLORE</label>
                    <input type="color" style={{ ...inputS,padding:'4px',height:'40px' }} value={newStatus.colore} onChange={e=>setNewStatus(f=>({...f,colore:e.target.value}))} />
                  </div>
                </div>
                <div style={{ display:'flex',gap:'16px',flexWrap:'wrap' }}>
                  {[['is_iniziale','STATO INIZIALE'],['is_conferma','PRENOTA MAGAZZINO'],['is_consegna','SCALA MAGAZZINO'],['is_finale','STATO FINALE']].map(([k,l]) => (
                    <label key={k} style={{ display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'var(--font-size-xs)',color:'var(--text-muted)' }}>
                      <input type="checkbox" checked={newStatus[k]||false} onChange={e=>setNewStatus(f=>({...f,[k]:e.target.checked}))} style={{ accentColor:'#F4600C' }}/>
                      {l}
                    </label>
                  ))}
                </div>
                <div style={{ display:'flex',gap:'10px',justifyContent:'flex-end' }}>
                  <button type="button" style={btnSec} onClick={()=>setShowNewStatus(false)}>ANNULLA</button>
                  <button type="submit" style={btnPri}>CREA STATO</button>
                </div>
              </form>
            </div>
          )}

          <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
            {orderStatuses.map((st, idx) => (
              <div key={st.id} style={{ background:'var(--bg-card)',border:`1px solid ${st.colore}30`,borderRadius:'10px',padding:'14px 18px',display:'flex',alignItems:'center',gap:'12px' }}>
                <div style={{ width:'12px',height:'12px',borderRadius:'50%',background:st.colore,flexShrink:0 }}/>
                {editingStatus?.id === st.id ? (
                  <div style={{ flex:1,display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap' }}>
                    <input style={{ ...inputS,width:'160px' }} value={editingStatus.nome} onChange={e=>setEditingStatus(f=>({...f,nome:e.target.value.toUpperCase()}))} />
                    <input type="color" style={{ ...inputS,width:'50px',padding:'3px',height:'34px' }} value={editingStatus.colore} onChange={e=>setEditingStatus(f=>({...f,colore:e.target.value}))} />
                    {[['is_conferma','Prenota'],['is_consegna','Scala'],['is_finale','Finale']].map(([k,l]) => (
                      <label key={k} style={{ display:'flex',alignItems:'center',gap:'5px',cursor:'pointer',fontSize:'var(--font-size-xs)',color:'var(--text-muted)' }}>
                        <input type="checkbox" checked={editingStatus[k]||false} onChange={e=>setEditingStatus(f=>({...f,[k]:e.target.checked}))} style={{ accentColor:'#F4600C' }}/>
                        {l}
                      </label>
                    ))}
                    <button style={{ ...btnSec,padding:'6px 12px' }} onClick={()=>setEditingStatus(null)}>ANN.</button>
                    <button style={{ ...btnPri,padding:'6px 12px' }} onClick={()=>saveStatus(st.id)}><Check size={13}/>SALVA</button>
                  </div>
                ) : (
                  <>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:'8px' }}>
                        <span style={{ fontWeight:'700',fontSize:'var(--font-size-sm)',color:st.colore }}>{st.nome}</span>
                        <span style={{ fontSize: 'var(--font-size-xs)',color:'var(--text-dim)' }}>#{idx+1}</span>
                        {st.is_iniziale && <span style={{ fontSize: 'var(--font-size-xs)',background:'#22C55E20',color:'#22C55E',border:'1px solid #22C55E30',borderRadius:'4px',padding:'1px 6px' }}>INIZIO</span>}
                        {st.is_conferma && <span style={{ fontSize: 'var(--font-size-xs)',background:'#3B82F620',color:'#3B82F6',border:'1px solid #3B82F630',borderRadius:'4px',padding:'1px 6px' }}>PRENOTA</span>}
                        {st.is_consegna && <span style={{ fontSize: 'var(--font-size-xs)',background:'#8B5CF620',color:'#8B5CF6',border:'1px solid #8B5CF630',borderRadius:'4px',padding:'1px 6px' }}>SCALA</span>}
                        {st.is_finale   && <span style={{ fontSize: 'var(--font-size-xs)',background:'#EF444420',color:'#EF4444',border:'1px solid #EF444430',borderRadius:'4px',padding:'1px 6px' }}>FINALE</span>}
                      </div>
                    </div>
                    <button style={{ background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'6px',padding:'5px 8px',color:'var(--text-muted)',cursor:'pointer' }}
                      onClick={()=>setEditingStatus({...st})}><Edit2 size={12}/></button>
                    {!st.is_iniziale && (
                      <button style={{ background:'#EF444415',border:'1px solid #EF444430',borderRadius:'6px',padding:'5px 8px',color:'#EF4444',cursor:'pointer' }}
                        onClick={()=>deleteStatus(st.id)}><X size={12}/></button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SMTP ── */}
      {tab === 'smtp' && (
        <div style={{ maxWidth:'600px' }}>
          <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px',marginBottom:'8px' }}>EMAIL / SMTP</h2>
          <p style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginBottom:'20px', lineHeight:1.5 }}>
            Tre passi. <strong>ACCESSI SMTP</strong>: le credenziali con cui il gestionale entra nel server di posta. Meglio una casella creata solo per spedire (es. invio@), così le password delle caselle vere non stanno qui; la password viene cifrata e non si rivede più.
            <strong> MITTENTI</strong>: nome e indirizzo che vede chi riceve, ognuno collegato a un accesso (anche uno solo per tutti).
            <strong> CHI INVIA COSA</strong>: il mittente di ogni servizio.
          </p>

          <div style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#F4600C', margin:'0 0 10px' }}>ACCESSI SMTP</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'10px' }}>
            {accessi.map(a => (
              <div key={a.id} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'14px 18px', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:'200px' }}>
                    <div style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{a.nome}</div>
                    <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'2px' }}>{a.utente} su {a.host}:{a.porta} · password cifrata</div>
                    <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'2px' }}>
                      {a.mittenti.length ? `Usato da: ${a.mittenti.map(m => m.nome).join(', ')}` : 'Non usato direttamente da nessun mittente'}
                    </div>
                  </div>
                  <button style={{ ...btnSec, opacity: accProvaId===a.id?0.7:1 }} disabled={accProvaId===a.id} onClick={() => provaAccesso(a.id)} title="Entra nel server e controlla utente e password, senza mandare email">{accProvaId===a.id ? 'PROVA...' : 'PROVA ACCESSO'}</button>
                  <button style={btnSec} onClick={() => { if (accEditingId === a.id) { setAccEditingId(null); } else { setAccEditingId(a.id); setAccDraft({ nome:a.nome, host:a.host, porta:a.porta, utente:a.utente, password:'' }); } }}>{accEditingId === a.id ? 'CHIUDI' : 'MODIFICA'}</button>
                  <button style={{ ...btnSec, color:'#EF4444' }} onClick={() => eliminaAccesso(a)}><Trash2 size={13}/></button>
                </div>
                {accEditingId === a.id && formAccesso()}
              </div>
            ))}
            {accEditingId === 'new' && (
              <div style={{ background:'var(--bg-card)', border:'1px solid #F4600C40', borderRadius:'12px' }}>{formAccesso()}</div>
            )}
          </div>
          {accEditingId !== 'new' && <button style={{ ...btnSec, marginBottom:'24px' }} onClick={nuovoAccesso}><Plus size={13}/> NUOVO ACCESSO SMTP</button>}

          <div style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#F4600C', margin:'0 0 10px' }}>MITTENTI</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'10px' }}>
            {smtpConfigs.map(cfg => {
              const inModifica = smtpEditingId === cfg.id;
              const acc = accessi.find(a => a.id === cfg.accesso_id);
              const accPrincipale = accessi.find(a => a.id === smtpConfigs.find(c => c.chiave === 'DEFAULT')?.accesso_id);
              return (
                <div key={cfg.id} style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'14px 18px', flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:'200px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <span style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{cfg.nome}</span>
                        {cfg.chiave === 'DEFAULT' && <span style={{ fontSize:'10px', fontWeight:'700', color:'#777', background:'var(--bg-input)', padding:'1px 7px', borderRadius:'5px' }}>PRINCIPALE</span>}
                      </div>
                      <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'2px' }}>
                        {cfg.smtp_from || '—'} · parte con {acc ? acc.utente : `l'accesso del principale${accPrincipale ? ` (${accPrincipale.utente})` : ''}`}
                      </div>
                    </div>
                    <span style={{ fontSize:'10px', fontWeight:'700', padding:'3px 9px', borderRadius:'6px', color: cfg.smtp_enabled ? '#22C55E' : '#888', background: cfg.smtp_enabled ? '#22C55E15' : 'var(--bg-input)' }}>
                      {cfg.smtp_enabled ? 'ATTIVO' : 'SPENTO'}
                    </span>
                    <button style={btnSec} onClick={() => inModifica ? chiudiSmtp() : modificaSmtp(cfg)}>{inModifica ? 'CHIUDI' : 'MODIFICA'}</button>
                    {cfg.chiave !== 'DEFAULT' && (
                      <button style={{ ...btnSec, color:'#EF4444' }} onClick={() => eliminaSmtp(cfg.id, cfg.nome)}><Trash2 size={13}/></button>
                    )}
                  </div>
                  {inModifica && formMittente(cfg)}
                </div>
              );
            })}
            {smtpEditingId === 'new' && (
              <div style={{ background:'var(--bg-card)',border:'1px solid #F4600C40',borderRadius:'12px' }}>{formMittente(null)}</div>
            )}
          </div>
          {smtpEditingId !== 'new' && <button style={{ ...btnSec, marginBottom:'24px' }} onClick={nuovaSmtp}><Plus size={13}/> NUOVO MITTENTE</button>}

          <div style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#F4600C', margin:'0 0 10px' }}>CHI INVIA COSA</div>
          <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', marginBottom:'8px' }}>
            {smtpAssociazioni.map((a, i) => (
              <div key={a.servizio} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', borderTop: i ? '1px solid var(--border)' : 'none', flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:'180px' }}>
                  <div style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{a.label}</div>
                  <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'2px' }}>{a.descrizione}</div>
                  <div style={{ fontSize:'var(--font-size-xs)', color: a.effettiva?.attiva ? '#22C55E' : '#F59E0B', marginTop:'3px' }}>
                    {a.effettiva?.attiva ? `Oggi scrive da: ${a.effettiva.mittente || a.effettiva.nome}${a.effettiva.accesso ? ` (accesso ${a.effettiva.accesso})` : ''}` : 'Nessun mittente attivo: le email di questo servizio non partono'}
                  </div>
                </div>
                <select style={{ ...inputS, width:'auto', minWidth:'200px' }} value={a.smtp_config_id || ''} onChange={e => associaSmtp(a.servizio, e.target.value ? +e.target.value : null)}>
                  <option value="">Principale (predefinito)</option>
                  {smtpConfigs.filter(c => c.chiave !== 'DEFAULT').map(c => (
                    <option key={c.id} value={c.id}>{c.nome} — {c.smtp_from || 'senza indirizzo'}{c.smtp_enabled ? '' : ' (spento)'}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#F4600C', margin:'28px 0 6px' }}>EMAIL INVIATE</div>
          <p style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)', margin:'0 0 10px', lineHeight:1.5 }}>
            Ogni email che il gestionale prova a mandare, riuscita o no (ultime 200, conservate 12 mesi). Registro attivo dal 16/10/2026: prima non veniva tenuto.
          </p>
          {invii === null ? (
            <button style={btnSec} onClick={() => caricaInvii()}>MOSTRA EMAIL INVIATE</button>
          ) : (
            <>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'10px' }}>
                <select style={{ ...inputS, width:'auto' }} value={filtroInvii.esito} onChange={e => { const f = { ...filtroInvii, esito:e.target.value }; setFiltroInvii(f); caricaInvii(f); }}>
                  <option value="">Tutte</option><option value="OK">Partite</option><option value="ERRORE">Non partite</option>
                </select>
                <input style={{ ...inputS, flex:1, minWidth:'160px' }} placeholder="Cerca destinatario, oggetto, servizio…" value={filtroInvii.q}
                  onChange={e => setFiltroInvii(f => ({ ...f, q:e.target.value }))} onKeyDown={e => e.key === 'Enter' && caricaInvii()}/>
                <button style={btnSec} onClick={() => caricaInvii()}>AGGIORNA</button>
              </div>
              <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px' }}>
                {invii.length === 0 && <div style={{ padding:'14px 16px', fontSize:'var(--font-size-xs)', color:'var(--text-dim)' }}>Nessuna email registrata.</div>}
                {invii.map((m, i) => (
                  <div key={m.id} style={{ padding:'10px 16px', borderTop: i ? '1px solid var(--border)' : 'none', fontSize:'var(--font-size-xs)' }}>
                    <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontWeight:'700', padding:'1px 7px', borderRadius:'5px', color: m.esito === 'OK' ? '#22C55E' : '#EF4444', background: m.esito === 'OK' ? '#22C55E15' : '#EF444415' }}>{m.esito === 'OK' ? 'PARTITA' : 'NON PARTITA'}</span>
                      <span style={{ color:'var(--text-dim)' }}>{new Date(m.created_at).toLocaleString('it-IT', { dateStyle:'short', timeStyle:'short' })}</span>
                      <span style={{ color:'var(--text-dim)' }}>· {m.servizio} · da {m.mittente}</span>
                    </div>
                    <div style={{ marginTop:'3px' }}><strong>{m.destinatario}</strong> — {m.oggetto}</div>
                    {m.errore && <div style={{ marginTop:'3px', color:'#EF4444' }}>{m.errore}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── NUMERAZIONE ── */}
      {tab === 'docs' && (
        <div style={{ maxWidth:'400px' }}>
          <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-lg)',fontWeight:'700',letterSpacing:'2px',marginBottom:'20px' }}>NUMERAZIONE DOCUMENTI</h2>
          <div style={{ background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'12px',padding:'24px',display:'flex',flexDirection:'column',gap:'14px' }}>
            <div style={fieldS}><label style={labelS}>PREFISSO ORDINI VENDITA</label>
              <input style={inputS} value={companyForm.prefisso_ordini||''} onChange={e=>setCompanyForm(f=>({...f,prefisso_ordini:e.target.value.toUpperCase()}))} placeholder="ORD" />
            </div>
            <div style={fieldS}><label style={labelS}>PREFISSO ORDINI ACQUISTO</label>
              <input style={inputS} value={companyForm.prefisso_acquisti||''} onChange={e=>setCompanyForm(f=>({...f,prefisso_acquisti:e.target.value.toUpperCase()}))} placeholder="ACQ" />
            </div>
            <div style={{ background:'var(--bg-input)',borderRadius:'8px',padding:'12px',fontSize:'var(--font-size-xs)',color:'var(--text-dim)' }}>
              Esempio: <span style={{ color:'#F4600C',fontWeight:'700' }}>{companyForm.prefisso_ordini||'ORD'}-2026-0001</span>
            </div>
            <button style={btnPri} onClick={saveCompany}>SALVA</button>
          </div>
        </div>
      )}

      {/* ── ASPETTO ── */}
      {tab === 'appearance' && (
        <div style={{ maxWidth:'560px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
            <div>
              <h2 style={{ fontFamily:"'Barlow Condensed'",fontSize:'var(--font-size-xl)',fontWeight:'700',letterSpacing:'2px' }}>ASPETTO</h2>
              <p style={{ color:'var(--text-dim)',fontSize:'var(--font-size-xs)',marginTop:'4px' }}>Le preferenze sono personali — ogni utente ha le proprie impostazioni</p>
            </div>
          </div>

          {/* DIMENSIONE TESTO */}
          <div style={{ background:'var(--bg-card,#141414)',border:'1px solid var(--border,#1E1E1E)',borderRadius:'12px',padding:'20px',marginBottom:'14px' }}>
            <div style={{ ...labelS, marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}>
              <Type size={14}/> DIMENSIONE TESTO
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
              {[
                { id:'small',  label:'Piccolo', desc:'13px', sample:'Aa' },
                { id:'medium', label:'Medio',   desc:'15px', sample:'Aa' },
                { id:'large',  label:'Grande',  desc:'17px', sample:'Aa' },
              ].map(f => (
                <button key={f.id}
                  style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:'6px',padding:'14px',background: prefs.fontSize===f.id?'#F4600C20':'var(--bg-input,#1A1A1A)',border:`2px solid ${prefs.fontSize===f.id?'#F4600C':'var(--border2,#2A2A2A)'}`,borderRadius:'10px',cursor:'pointer' }}
                  onClick={() => updateTheme('fontSize', f.id)}>
                  <span style={{ fontSize: f.id==='small'?'18px':f.id==='medium'?'22px':'28px', fontWeight:'700', color: prefs.fontSize===f.id?'#F4600C':'var(--text,#fff)', lineHeight:'1' }}>{f.sample}</span>
                  <span style={{ fontWeight:'700', fontSize:'var(--font-size-xs)', color: prefs.fontSize===f.id?'#F4600C':'var(--text,#fff)' }}>{f.label}</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-muted)' }}>{f.desc}</span>
                  {prefs.fontSize===f.id && <Check size={13} color="#F4600C"/>}
                </button>
              ))}
            </div>
          </div>

          {/* MODALITÀ COMPATTA */}
          <div style={{ background:'var(--bg-card,#141414)',border:'1px solid var(--border,#1E1E1E)',borderRadius:'12px',padding:'20px' }}>
            <div style={{ ...labelS, marginBottom:'14px' }}>DENSITÀ INTERFACCIA</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              {[
                { id:false, label:'Normale',  desc:'Spaziatura standard, più leggibile' },
                { id:true,  label:'Compatta', desc:'Meno spazio, più elementi visibili' },
              ].map(m => (
                <button key={String(m.id)}
                  style={{ display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'4px',padding:'14px 16px',background: prefs.compactMode===m.id?'#F4600C20':'var(--bg-input,#1A1A1A)',border:`2px solid ${prefs.compactMode===m.id?'#F4600C':'var(--border2,#2A2A2A)'}`,borderRadius:'10px',cursor:'pointer' }}
                  onClick={() => updateTheme('compactMode', m.id)}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontWeight:'700', fontSize:'var(--font-size-sm)', color: prefs.compactMode===m.id?'#F4600C':'var(--text,#fff)' }}>{m.label}</span>
                    {prefs.compactMode===m.id && <Check size={13} color="#F4600C"/>}
                  </div>
                  <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{m.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ANTEPRIMA */}
          <div style={{ marginTop:'16px', background:'var(--bg-card,#141414)',border:'1px solid #F4600C30',borderRadius:'12px',padding:'16px' }}>
            <div style={{ ...labelS, marginBottom:'10px', color:'#F4600C' }}>ANTEPRIMA</div>
            <div style={{ fontSize:'var(--font-size-base,15px)', color:'var(--text,#fff)', marginBottom:'4px', fontWeight:'600' }}>Titolo esempio in dimensione base</div>
            <div style={{ fontSize:'var(--font-size-sm,13px)', color:'var(--text-muted,#888)' }}>Testo secondario come nelle descrizioni e nelle etichette dei campi</div>
            <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
              <div style={{ background:'#F4600C20', border:'1px solid #F4600C40', borderRadius:'6px', padding:'4px 10px', fontSize:'var(--font-size-xs,11px)', color:'#F4600C', fontWeight:'700' }}>BADGE</div>
              <div style={{ background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'4px 12px', fontSize:'var(--font-size-sm,13px)', color:'var(--text,#fff)' }}>Input example</div>
            </div>
          </div>

          {/* SALVA */}
          <div style={{ marginTop:'20px', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
            <button style={{ ...btnSec, fontSize:'var(--font-size-xs)', padding:'6px 12px' }} onClick={resetTheme}>RIPRISTINA DEFAULT</button>
            <button style={{ ...btnPri, gap:'8px' }} onClick={() => toast('Impostazioni aspetto salvate!')}>
              <Check size={13}/> SALVA IMPOSTAZIONI
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
